import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/components/PageHeader";
import { CHIP, summarize, AttendanceCounts, RecordRow } from "@/components/MemberAttendanceCard";
import api from "@/lib/api";
import { formatDateOnly } from "@/lib/date";

const FILTERS = [["all", "All"], ["absent", "Absences"], ["tardy", "Tardies"], ["excused", "Excused"]];

function Filters({ filter, setFilter, counts, from, setFrom, to, setTo }) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div className="flex flex-wrap gap-2" data-testid="attendance-history-filters">
        {FILTERS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`attendance-history-filter-${k}`}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${filter === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
            {label} <span className="ml-1 tabular-nums opacity-70">{counts[k]}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1"><Label className="text-xs text-slate-500">From</Label><Input type="date" className="h-9 w-40" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} data-testid="attendance-history-from" /></div>
        <div className="space-y-1"><Label className="text-xs text-slate-500">To</Label><Input type="date" className="h-9 w-40" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} data-testid="attendance-history-to" /></div>
        {(from || to) && <Button variant="ghost" size="sm" className="h-9 rounded-full text-slate-500" onClick={() => { setFrom(""); setTo(""); }} data-testid="attendance-history-clear-dates"><X className="mr-1 h-3.5 w-3.5" /> Clear dates</Button>}
      </div>
    </div>
  );
}

// Daily Schedule → Attendance tab: every record with status chips + date range filter.
// Parents pass their student's `records`; members load their own.
export default function MemberAttendanceHistory({ records: given }) {
  const [own, setOwn] = useState(null);
  const [filter, setFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  useEffect(() => { if (!given) api.get("/attendance/my").then(({ data }) => setOwn(data.records || [])).catch(() => setOwn([])); }, [given]);
  const records = given || own;
  if (records === null) return null;

  const inRange = records.filter((r) => (!from || r.event_date >= from) && (!to || r.event_date <= to));
  const s = summarize(inRange);
  const marked = inRange.filter((r) => r.status !== "present");
  const shown = filter === "all" ? marked : marked.filter((r) => r.status === filter);

  return (
    <div data-testid="member-attendance-history">
      <Card className="mb-4 border-slate-200 p-5 shadow-sm" data-testid="attendance-history-summary">
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">{from || to ? "Selected dates" : "All time"}</p>
        <AttendanceCounts s={s} testPrefix="attendance-history" />
      </Card>
      <Filters filter={filter} setFilter={setFilter} counts={{ all: marked.length, absent: s.absent, tardy: s.tardy, excused: s.excused }} from={from} setFrom={setFrom} to={to} setTo={setTo} />
      {shown.length === 0 ? (
        <EmptyState title="No tardies, absences or excused records" description={records.length ? "Nothing matches these filters." : "Attendance will appear here once the coach takes it."} testId="attendance-history-empty" />
      ) : (
        <>
          <Card className="hidden overflow-hidden border-slate-200 shadow-sm md:block">
            <Table data-testid="attendance-history-table">
              <TableHeader><TableRow className="bg-slate-50">{["Date", "Event", "Status", "Note"].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {shown.map((r, i) => (
                  <TableRow key={`${r.event_id}-${i}`} data-testid={`attendance-history-row-${r.event_id}`}>
                    <TableCell className="whitespace-nowrap text-sm tabular-nums">{formatDateOnly(r.event_date)}</TableCell>
                    <TableCell className="text-sm font-medium text-slate-800">{r.event_title || "Event"}</TableCell>
                    <TableCell><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${CHIP[r.status] || CHIP.present}`} data-testid={`attendance-history-status-${r.event_id}`}>{r.status}</span></TableCell>
                    <TableCell className="max-w-sm text-sm italic text-slate-500">{r.note}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="divide-y divide-slate-100 border-slate-200 px-4 shadow-sm md:hidden" data-testid="attendance-history-cards">
            {shown.map((r, i) => <RecordRow key={`${r.event_id}-${i}`} r={r} testId={`attendance-history-card-${r.event_id}`} />)}
          </Card>
        </>
      )}
    </div>
  );
}
